
import { useState, useEffect } from 'react';

export default function AtelierHero() {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const targetDate = new Date('2026-03-01T00:00:00+09:00');

    const calc = () => {
      const diff = targetDate.getTime() - Date.now();
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      } else {
        // Countdown finished – clear values
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calc(); // initialise immediately
    const timer = setInterval(calc, 1000);
    return () => clearInterval(timer);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <section
      className="relative min-h-screen flex items-center pt-20 pb-16 overflow-hidden bg-[#0A0A0A]"
      aria-labelledby="af-hero-heading"
    >
      {/* BG */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/3 w-[700px] h-[700px] bg-[#8E7AB5]/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#8E7AB5]/5 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#06C755] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#06C755]"></span>
              </span>
              <span className="text-xs text-white/60 font-light tracking-wider">
                2026年3月 Makuake公開予定
              </span>
            </div>

            <h1
              id="af-hero-heading"
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 tracking-tight leading-[1.1]"
            >
              Atelier Folio<br />
              <span className="text-[#8E7AB5]">for iPad</span>
            </h1>

            <p className="text-lg sm:text-xl text-white/50 font-light mb-8 leading-relaxed max-w-lg">
              iPadを、あなたのアトリエに。<br />
              クリエイティブワークに特化した、革新的なiPadスタンド＆フォリオケース。
            </p>

            {/* Countdown */}
            <div className="mb-8">
              <p className="text-xs text-white/30 font-light tracking-wider uppercase mb-3">
                Makuake公開まで
              </p>
              <div className="flex items-center gap-2 sm:gap-3">
                {[
                  { value: timeLeft.days, label: '日' },
                  { value: timeLeft.hours, label: '時' },
                  { value: timeLeft.minutes, label: '分' },
                  { value: timeLeft.seconds, label: '秒' },
                ].map((item, i) => (
                  <div key={item.label} className="flex items-center gap-2 sm:gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-white/5 border border-white/10 rounded-lg">
                        <span
                          className={`text-xl sm:text-2xl font-bold ${
                            item.label === '秒' ? 'text-[#8E7AB5]' : 'text-white'
                          }`}
                        >
                          {pad(item.value)}
                        </span>
                      </div>
                      <span className="text-[10px] text-white/25 mt-1.5">{item.label}</span>
                    </div>
                    {i < 3 && <span className="text-white/15 text-lg font-light mb-4">:</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* LINE CTA */}
            <a
              href="https://lin.ee/d6PoCFQ6"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center justify-center gap-2.5 w-full sm:w-auto px-8 py-4 bg-[#06C755] hover:bg-[#05B34A] text-white text-base font-medium tracking-wide transition-all duration-300 rounded-xl cursor-pointer whitespace-nowrap hover:shadow-lg hover:shadow-[#06C755]/25 group"
            >
              <i className="ri-line-fill text-2xl" aria-hidden="true"></i>
              <span>公式LINEで事前登録する</span>
              <i
                className="ri-arrow-right-s-line text-lg group-hover:translate-x-1 transition-transform"
                aria-hidden="true"
              ></i>
            </a>
            <p className="text-[11px] text-white/20 mt-3 font-light">
              無料・いつでもブロック可能
            </p>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10">
              <img
                src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/432bdd1009cdd155013d836f9a744018.jpeg?format=webp&quality=80"
                alt="Atelier Folio for iPad"
                className="w-full h-full object-cover object-center"
                loading="eager"
                width={600}
                height={600}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/30 via-transparent to-transparent" />
            </div>

            {/* Floating badges */}
            <div className="absolute -bottom-4 -right-2 sm:bottom-6 sm:right-6 flex flex-col gap-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-lg">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-pencil-ruler-2-line text-[#8E7AB5] text-sm" aria-hidden="true"></i>
                </div>
                <span className="text-xs text-white/80 font-light">無段階角度調整</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-lg">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-pencil-line text-[#8E7AB5] text-sm" aria-hidden="true"></i>
                </div>
                <span className="text-xs text-white/80 font-light">Apple Pencilホルダー</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-lg">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-hand-heart-line text-[#8E7AB5] text-sm" aria-hidden="true"></i>
                </div>
                <span className="text-xs text-white/80 font-light">プレミアムレザー</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
